import { useTranslation } from 'react-i18next';
import { Flex, Form, Input, Modal, Select, Switch, Typography } from 'antd';
import type { FormInstance } from 'antd';
import {
  AssetCustomFieldType,
  type AssetCustomFieldType as AssetCustomFieldTypeValue,
} from '@evimo/types/enums/asset-custom-field-type';
import type { FindAssetCustomFieldResponseDto } from '@evimo/types/api/asset-custom-field/find-asset-custom-field/find-asset-custom-field.response.dto';
import type { AssetCustomFieldUnitOptionGroup } from '../../constants/asset-custom-field-units';
import { AssetCustomFieldFormValue } from '~/app/types/assetCustomFieldFormValue';

const { TextArea } = Input;
const { Text } = Typography;

type AssetCustomFieldModalProps = {
  open: boolean;
  editingField: FindAssetCustomFieldResponseDto | null;
  fieldForm: FormInstance<AssetCustomFieldFormValue>;
  fieldTypeOptions: Array<{
    value: AssetCustomFieldTypeValue;
    label: string;
  }>;
  unitOptions: AssetCustomFieldUnitOptionGroup[];
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
};

export function AssetCustomFieldModal({
  open,
  editingField,
  fieldForm,
  fieldTypeOptions,
  unitOptions,
  isSubmitting,
  onSubmit,
  onCancel,
}: AssetCustomFieldModalProps) {
  const { t } = useTranslation('assetCustomFields');
  const selectedFieldType = Form.useWatch('fieldType', fieldForm);
  const isNumberField =
    (selectedFieldType ?? editingField?.fieldType) ===
    AssetCustomFieldType.NUMBER;

  return (
    <Modal
      title={
        editingField
          ? t('modals.editFieldTitle', 'Edit Custom Field')
          : t('modals.createFieldTitle', 'Create Custom Field')
      }
      open={open}
      onOk={onSubmit}
      onCancel={onCancel}
      okText={
        editingField ? t('actions.save', 'Save') : t('actions.create', 'Create')
      }
      cancelText={t('actions.cancel', 'Cancel')}
      confirmLoading={isSubmitting}
      destroyOnHidden
    >
      <Form<AssetCustomFieldFormValue>
        form={fieldForm}
        layout="vertical"
        initialValues={{
          required: false,
          fieldType: AssetCustomFieldType.TEXT,
        }}
      >
        {/* Field Name */}
        <Form.Item
          name="name"
          label={t('form.fieldName', 'Field Name')}
          rules={[
            {
              required: true,
              message: t(
                'validation.fieldNameRequired',
                'Field name is required',
              ),
            },
          ]}
        >
          <Input />
        </Form.Item>

        {/* Field Key */}
        <Form.Item
          name="fieldKey"
          label={t('form.fieldKey', 'Field Key')}
          tooltip={t(
            'form.fieldKeyHelp',
            'Unique internal key. It cannot be changed after creation.',
          )}
          rules={
            editingField
              ? []
              : [
                  {
                    required: true,
                    message: t(
                      'validation.fieldKeyRequired',
                      'Field key is required',
                    ),
                  },
                ]
          }
        >
          <Input
            disabled={Boolean(editingField)}
            suffix={
              editingField ? (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t('form.fieldKeyLocked', 'locked')}
                </Text>
              ) : null
            }
          />
        </Form.Item>

        {/* Field Type + Required — même ligne */}
        <Flex gap="middle">
          <Form.Item
            name="fieldType"
            label={t('form.fieldType', 'Field Type')}
            style={{ flex: 1 }}
            rules={
              editingField
                ? []
                : [
                    {
                      required: true,
                      message: t(
                        'validation.fieldTypeRequired',
                        'Field type is required',
                      ),
                    },
                  ]
            }
          >
            <Select
              disabled={Boolean(editingField)}
              options={fieldTypeOptions}
            />
          </Form.Item>

          <Form.Item
            name="required"
            label={t('form.required', 'Required')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Flex>

        {isNumberField && (
          <Form.Item
            name="unit"
            label={t('form.unit', 'Unit')}
            extra={t(
              'form.unitHelp',
              'Optional display unit for numeric values. Choose from the predefined list.',
            )}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={unitOptions}
            />
          </Form.Item>
        )}

        {/* Rules JSON */}
        <Form.Item
          name="rulesText"
          label={t('form.rules', 'Rules (JSON)')}
          extra={t(
            'form.rulesHelp',
            'Optional JSON object. Example: {"min": 0, "max": 100}',
          )}
        >
          <TextArea
            placeholder='{"min": 0, "max": 100}'
            autoSize={{ minRows: 4, maxRows: 8 }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
